'use client';

import { useEffect, useRef, useState } from 'react';

import { useAtomValue } from 'jotai';

import { compareAtom } from '@core/Atoms/CarCompare/Compare.atom';
import { Compare } from '@gds/Icons';
import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';

import { TrackedLink } from '../../TrackedLink/TrackedLink';

import stylesCompareIcon from './CompareIconButton.module.css';
import styles from '../NavButtons.module.css';

type Props = {
  baseUrl: string;
  locale: string;
};

export const CompareIconButton = ({ baseUrl, locale }: Props) => {
  const vehiclesToCompare = useAtomValue(compareAtom);
  const compareVehiclesCount = vehiclesToCompare?.length ?? 0;

  const [counterClass, setCounterClass] = useState('');
  const [counterNumberClass, setCounterNumberClass] = useState('');
  const [rectangleClass, setRectangleClass] = useState('');
  const [hasMounted, setHasMounted] = useState(false);
  const prevCompareCountRef = useRef(compareVehiclesCount);

  const headerTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const ANIMATION_DURATION = 1400;

  // Function to show header by adding local class to body
  const showHeader = () => {
    document.body.classList.add(stylesCompareIcon.showHeader);
  };

  // Function to hide header by removing local class from body
  const hideHeader = () => {
    document.body.classList.remove(stylesCompareIcon.showHeader);
  };

  useEffect(() => {
    setHasMounted(true);
    prevCompareCountRef.current = compareVehiclesCount;
    if (compareVehiclesCount > 0) {
      setCounterClass(stylesCompareIcon.counterVisible);
      setCounterNumberClass(stylesCompareIcon.counterNumberVisible);
    } else {
      setCounterClass(stylesCompareIcon.counterHidden);
      setCounterNumberClass(stylesCompareIcon.counterNumberHidden);
    }
  }, []);

  // Effect to handle animations and header visibility based on count changes
  useEffect(() => {
    if (!hasMounted) {
      return;
    }

    const prevCount = prevCompareCountRef.current;
    const currentCount = compareVehiclesCount;

    const isIncrementing = currentCount > prevCount;
    const isDecrementing = currentCount < prevCount;

    let timeoutId: NodeJS.Timeout | null = null;

    // Only apply header logic on mobile (screen width <= 768px)
    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    if (isMobile && (isIncrementing || isDecrementing)) {
      // Show header before animation
      showHeader();

      // Clear any existing header timeout
      if (headerTimeoutRef.current) {
        clearTimeout(headerTimeoutRef.current);
      }

      // Hide header after animation completes
      headerTimeoutRef.current = setTimeout(() => {
        hideHeader();
      }, ANIMATION_DURATION);
    }

    if (isIncrementing || isDecrementing) {
      // Clear the rectangle class to reset the animation
      setRectangleClass('');

      // Reapply the animation class after a short delay
      timeoutId = setTimeout(() => {
        if (isIncrementing) {
          setRectangleClass(stylesCompareIcon.rectangleAnimateIn);
        } else if (isDecrementing) {
          setRectangleClass(stylesCompareIcon.rectangleAnimateOut);
        }
      }, 50); //This is to ensure that browser registers the change
    }

    // Update counter visibility
    if (isIncrementing) {
      setCounterClass(stylesCompareIcon.counterVisible);
      setCounterNumberClass(stylesCompareIcon.counterNumberVisible);
    } else if (isDecrementing) {
      if (currentCount === 0) {
        setCounterNumberClass(stylesCompareIcon.counterNumberHidden);
        setCounterClass(stylesCompareIcon.counterHidden);
      } else {
        setCounterClass(stylesCompareIcon.counterVisible);
        setCounterNumberClass(stylesCompareIcon.counterNumberVisible);
      }
    }

    prevCompareCountRef.current = currentCount;

    // Cleanup function for timeouts
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (headerTimeoutRef.current) {
        clearTimeout(headerTimeoutRef.current);
      }
    };
  }, [compareVehiclesCount, hasMounted]);

  const compareHref = locale === 'fr' ? `${baseUrl}/comparatif-auto` : `${baseUrl}/car-compare`;
  const compareItems = {
    favoritesNumber: compareVehiclesCount,
    href: compareHref,
    label: 'Compare',
  };
  const flags = useAtomValue(flagsAtom);
  if (!flags?.carCompare) return null;

  return (
    <TrackedLink
      href={compareItems.href}
      className={`${styles.item} ${stylesCompareIcon.compareIconLinkWrapper || ''}`}
      trackingObj={{ label: compareItems.label, href: compareItems.href }}
      aria-label={`Compare ${compareVehiclesCount} vehicles`}
    >
      <span className={stylesCompareIcon.iconWrapper}>
        <span className={`${stylesCompareIcon.rectangle} ${rectangleClass}`}></span>
        <Compare />
        {compareVehiclesCount > 0 && (
          <span className={`${stylesCompareIcon.counter} ${counterClass}`}>
            <span className={`${stylesCompareIcon.counterNumber} ${counterNumberClass}`}>
              {compareVehiclesCount}
            </span>
          </span>
        )}
      </span>
    </TrackedLink>
  );
};
